import makeDebug from '../../../makeDebug';

const debug = makeDebug('sync:metrics');

/**
 * Maximale Anzahl gespeicherter Einzelmessungen. Darüber hinaus werden nur noch die
 * Aggregate fortgeschrieben, damit ein Dauerlauf keinen Speicher aufbraucht.
 */
export const MAX_METRIC_SAMPLES = 5000;

/** Art der Messung, bestimmt wie der Zahlenwert zu lesen ist. */
export type MetricKind = 'span' | 'count' | 'value';

/** Laufende Messung einer Dauer. */
export interface IMetricSpan {
  /** Beendet die Messung und schreibt die Dauer fort. */
  end(): void;
}

/** Zusammenfassung aller Messungen gleichen Namens. */
export interface IMetricAggregate {
  name: string;
  kind: MetricKind;
  /** Anzahl der Einzelmessungen. */
  observations: number;
  /** Summe der Werte; bei `span` die Gesamtdauer in Millisekunden. */
  sum: number;
  min: number;
  max: number;
}

/**
 * Einzelmessung mit Zeitbezug. Die Aggregate sagen nur, wie viel insgesamt anfiel; erst die
 * Rohproben zeigen Reihenfolge und Überlappung — und damit, welcher Vorgang den kritischen
 * Pfad bildet und ob späte Seiten langsamer werden als frühe.
 */
export interface IMetricSample {
  name: string;
  kind: MetricKind;
  /** Zeit seit Seitenstart zu **Beginn** der Messung, in Millisekunden. */
  atMs: number;
  /** Bei `span` die Dauer in Millisekunden, sonst der beobachtete Wert. */
  value: number;
}

/** Ergebnis eines Flush-Vorgangs. */
export interface IMetricsReport {
  reason: string;
  /** Zeit seit Seitenstart zum Zeitpunkt des Flush, in Millisekunden. */
  atMs: number;
  aggregates: IMetricAggregate[];
  /** Einzelmessungen in Startreihenfolge, gedeckelt auf `MAX_METRIC_SAMPLES`. */
  samples: IMetricSample[];
  /** True, sobald `MAX_METRIC_SAMPLES` überschritten wurde. */
  truncated: boolean;
}

declare global {
  interface Window {
    /**
     * Letzter Messbericht, allein zum Herausholen aus der Konsole: `copy(window.lastMetricsReport)`.
     * Die Wertetabelle im Log lässt sich weder zuverlässig kopieren noch übersteht sie ein
     * `Console cleared`.
     */
    lastMetricsReport: IMetricsReport | null;
  }
}

const NOOP_SPAN: IMetricSpan = { end: (): void => undefined };

let enabled: boolean = debug.enabled;
let aggregates = new Map<string, IMetricAggregate>();
let samples: IMetricSample[] = [];
let truncated = false;

/**
 * Schaltet die Messung zur Laufzeit. Vorbelegt aus dem Debug-Namespace `sync:metrics`,
 * der über `localStorage.debug` gesetzt wird.
 */
export function setMetricsEnabled(value: boolean): void {
  enabled = value;
}

/**
 * Gibt an, ob gemessen wird. Teure Erhebungen — etwa das Zählen von Datenbankzeilen —
 * gehören hinter diese Abfrage, weil nur die Aufzeichnung billig ist, nicht die Beschaffung.
 */
export function isMetricsEnabled(): boolean {
  return enabled;
}

/**
 * Startet eine Dauermessung.
 * @param name Pfadartiger Name, etwa `sync/patient/find`
 */
export function startSpan(name: string): IMetricSpan {
  if (!enabled) {
    return NOOP_SPAN;
  }

  const startedAt = performance.now();
  return {
    end: (): void => observe(name, 'span', performance.now() - startedAt, startedAt),
  };
}

/**
 * Zählt ein Ereignis, etwa einen abgesetzten Request.
 * @param name Pfadartiger Name
 * @param amount Zu addierender Betrag, vorbelegt mit 1
 */
export function countMetric(name: string, amount: number = 1): void {
  observe(name, 'count', amount);
}

/**
 * Hält einen Einzelwert fest, etwa eine vom Server vorgegebene Seitengröße.
 */
export function recordValue(name: string, value: number): void {
  observe(name, 'value', value);
}

/**
 * Fasst alle bisherigen Messungen zusammen, gibt sie aus und setzt die Aufzeichnung zurück.
 * Während eines Laufs wird nur gesammelt — die Ausgabe erfolgt einmal am Ende, weil
 * `console.log` im WKWebView pro Aufruf über die native Bridge geht und die Messung sonst
 * genau das verfälscht, was sie erfassen soll.
 *
 * @returns Den Report, oder `null` wenn die Messung abgeschaltet ist
 */
export function flushMetrics(reason: string): IMetricsReport | null {
  if (!enabled) {
    return null;
  }

  const report: IMetricsReport = {
    reason,
    atMs: Math.round(performance.now()),
    aggregates: [...aggregates.values()].sort((left, right) => left.name.localeCompare(right.name)),
    samples: [...samples].sort((left, right) => left.atMs - right.atMs),
    truncated,
  };

  // Bewusst nicht in die Konsole: bis zu MAX_METRIC_SAMPLES Zeilen auszugeben würde länger
  // dauern als manche der gemessenen Vorgänge. Abholbar über `copy(window.lastMetricsReport)`.
  window.lastMetricsReport = report;

  debug('report (%s) nach %d ms, %d Rohproben', report.reason, report.atMs, report.samples.length);
  console.table(
    report.aggregates.map(aggregate => ({
      Messpunkt: aggregate.name,
      Art: aggregate.kind,
      Anzahl: aggregate.observations,
      Summe: round(aggregate.sum),
      Min: round(aggregate.min),
      Max: round(aggregate.max),
    }))
  );

  resetMetrics();
  return report;
}

/** Verwirft alle bisherigen Messungen. */
export function resetMetrics(): void {
  aggregates = new Map<string, IMetricAggregate>();
  samples = [];
  truncated = false;
}

function observe(name: string, kind: MetricKind, observation: number, atMs: number = performance.now()): void {
  if (!enabled) {
    return;
  }

  if (samples.length < MAX_METRIC_SAMPLES) {
    samples.push({ name, kind, atMs: round(atMs), value: round(observation) });
  } else {
    truncated = true;
  }

  const existing = aggregates.get(name);
  if (!existing) {
    aggregates.set(name, {
      name,
      kind,
      observations: 1,
      sum: observation,
      min: observation,
      max: observation,
    });
    return;
  }

  existing.observations += 1;
  existing.sum += observation;
  existing.min = Math.min(existing.min, observation);
  existing.max = Math.max(existing.max, observation);
}

function round(value: number): number {
  return Math.round(value * 100) / 100;
}
